import React, { useEffect, useMemo, useState } from 'react'

import type { Invoice } from '../types/invoice'
import { useCompanyOptionsQuery } from '../hooks/useCompanyOptionsQuery'
import { useDealQuery } from '../hooks/useDealQuery'
import { useInvoicesQuery } from '../hooks/useInvoicesQuery'
import { useSendInvoiceMutation } from '../hooks/useSendInvoiceMutation'
import { useModal } from '../state/ModalProvider'
import { formatCents } from '../utils/formatCents'

import { DEFAULT_MESSAGE, dealCcFor, dealEmailFor, joinCcList, parseCcList } from './invoiceEmail'

interface Props {
  dealId: string
}

interface SendForm {
  to: string
  cc: string
  subject: string
  message: string
}

const READONLY_INPUT_STYLE: React.CSSProperties = {
  background: '#f5f5f7',
  color: '#6e6e73',
  cursor: 'not-allowed',
}

function pickFirstInvoice(invoices: Invoice[]): Invoice | null {
  if (invoices.length === 0) return null
  return (
    invoices.find((entry) => entry.origin === 'auto' && entry.status === 'Draft') ??
    invoices.find((entry) => entry.status === 'Draft') ??
    null
  )
}

function formatTotalChip(invoice: Invoice | null): string {
  if (!invoice) return ''
  return formatCents(invoice.totalCents)
}

export const FirstInvoiceModal: React.FunctionComponent<Props> = (props) => {
  const { dealId } = props
  const { close } = useModal()
  const dealQuery = useDealQuery(dealId)
  const invoicesQuery = useInvoicesQuery(dealId)
  const companyOptionsQuery = useCompanyOptionsQuery()
  const sendMutation = useSendInvoiceMutation()

  const deal = dealQuery.data ?? null
  const invoices = invoicesQuery.data ?? []
  const invoice = useMemo(() => pickFirstInvoice(invoices), [invoices])
  const companyName = useMemo(() => {
    if (!deal) return ''
    return (
      companyOptionsQuery.data?.find((option) => option.id === deal.companyId)?.name ??
      deal.companyId
    )
  }, [companyOptionsQuery.data, deal])

  // Idempotency-Key only flips when the modal is reopened so retries within
  // the same modal lifetime collapse on the backend.
  const idempotencyKey = useMemo(
    () =>
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    [],
  )

  const [form, setForm] = useState<SendForm>({
    to: '',
    cc: '',
    subject: '',
    message: DEFAULT_MESSAGE,
  })
  const [didPrefill, setDidPrefill] = useState(false)

  useEffect(() => {
    if (didPrefill) return
    if (!deal || !invoice) return
    setForm({
      to: dealEmailFor(deal, companyName),
      cc: joinCcList(dealCcFor(deal)),
      subject: `Invoice ${invoice.invoiceNumber} from Calyx`,
      message: DEFAULT_MESSAGE,
    })
    setDidPrefill(true)
  }, [didPrefill, deal, invoice, companyName])

  const isSubmitting = sendMutation.isLoading

  // Loading: deal or invoices list still resolving.
  if (!deal || invoicesQuery.isLoading) {
    return (
      <div className="send-inv-overlay active" onClick={close}>
        <div className="send-inv-dialog" onClick={(event) => event.stopPropagation()}>
          <div className="send-inv-header">
            <h3>Send first invoice</h3>
            <div className="sub">Loading first invoice…</div>
          </div>
          <div className="send-inv-footer" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Empty fallback: query resolved but backend race / not yet built.
  if (!invoice) {
    return (
      <div className="send-inv-overlay active" onClick={close}>
        <div className="send-inv-dialog" onClick={(event) => event.stopPropagation()}>
          <div className="send-inv-header">
            <h3>Send first invoice</h3>
            <div className="sub">
              No first invoice yet — backend may still be processing. Close this and check the
              deal page in a moment.
            </div>
          </div>
          <div className="send-inv-footer" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" onClick={close}>
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  const totalChip = formatTotalChip(invoice)

  function handleSend(): void {
    if (isSubmitting || !invoice) return
    const trimmed = {
      to: form.to.trim(),
      cc: parseCcList(form.cc),
      subject: form.subject.trim(),
      message: form.message,
    }
    if (!trimmed.to) return
    sendMutation.mutate(
      { dealId, invoiceId: invoice.id, body: trimmed, idempotencyKey },
      {
        onSuccess: () => {
          close()
        },
      },
    )
  }

  return (
    <div className="send-inv-overlay active" onClick={close}>
      <div className="send-inv-dialog" onClick={(event) => event.stopPropagation()}>
        <div className="send-inv-header">
          <h3>Send first invoice</h3>
          <div className="sub">The first invoice for this deal is ready to send.</div>
        </div>
        <div className="send-inv-body">
          <div className="send-inv-summary">
            <span className="inv-num">{invoice.invoiceNumber}</span>
            <span className="dot">·</span>
            <span>{companyName}</span>
            {totalChip ? (
              <>
                <span className="dot">·</span>
                <span className="total">{totalChip}</span>
              </>
            ) : null}
          </div>
          <div className="fg">
            <label>To</label>
            <input type="email" value={form.to} readOnly style={READONLY_INPUT_STYLE} />
          </div>
          <div className="fg">
            <label>
              CC <span style={{ color: '#6e6e73', fontWeight: 400 }}>(optional)</span>
            </label>
            <input type="text" value={form.cc} readOnly style={READONLY_INPUT_STYLE} />
          </div>
          <div className="fg">
            <label>Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(event) =>
                setForm((current) => ({ ...current, subject: event.target.value }))
              }
            />
          </div>
          <div className="fg">
            <label>Message</label>
            <textarea
              value={form.message}
              onChange={(event) =>
                setForm((current) => ({ ...current, message: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="send-inv-footer" style={{ justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-ghost" onClick={close} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!form.to.trim() || isSubmitting || !invoice}
              onClick={handleSend}
            >
              Send now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
